<template>
  <div class="user-item">
    <div class="user-item__left">
      <div class="user-item__avatar">
        <span>{{ initials }}</span>
      </div>
      <div class="user-item__info">
        <p class="user-item__name">
          {{ user.nombre || 'Sin nombre' }}
        </p>
        <p class="user-item__email">
          {{ user.email }}
        </p>
      </div>
    </div>

    <div class="user-item__middle">
      <span class="user-item__badge">
        {{ roleLabel }}
      </span>
      <span
        v-if="user.group_name"
        class="user-item__group"
      >
        {{ user.group_name }} 
      </span>
    </div>

    <div class="user-item__right">
      <span
        class="user-item__status"
        :class="user.activo === '1' || user.activo === 1 ? 'is-active' : 'is-inactive'"
      >
        {{ user.activo === '1' || user.activo === 1 ? 'Activo' : 'Inactivo' }}
      </span>

      <button
        v-if="canEdit"
        type="button"
        class="user-item__edit-btn"
        @click="$emit('edit', user)"
      >
        ✏️
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserListItem',
  props: {
    user: {
      type: Object,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    initials() {
      const name = this.user.nombre || '';
      const parts = name.split(' ').filter(Boolean);
      const first = parts[0]?.[0] || '';
      const second = parts[1]?.[0] || '';
      const i = (first + second).toUpperCase();
      return i || 'U';
    },
    roleLabel() {
      const map = {
        superadministrador: 'Super Administrador',
        administrador: 'Administrador',
        usuario: 'Usuario',
        repartidor: 'repartidor',
        recolector: 'Recolector',
      };
      return map[this.user.role] || '—';
    },
  },
};
</script>

<style scoped>
.user-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.86);
  border: 1px solid rgba(148, 163, 184, 0.4);
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.7);
}

.user-item__left {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  min-width: 0; /* importante para que el texto pueda recortar */
}

.user-item__avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: radial-gradient(
    circle at 0 0,
    rgba(148, 163, 184, 0.28),
    rgba(15, 23, 42, 1)
  );
  border: 1px solid rgba(148, 163, 184, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #e5e7eb;
}

.user-item__info {
  min-width: 0;
}

.user-item__name {
  font-size: 0.9rem;
  color: #e5e7eb;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.user-item__email {
  font-size: 0.75rem;
  color: #94a3b8;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

/* MIDDLE: rol + grupo, alineado a la derecha en desktop */
.user-item__middle {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  min-width: 140px;
  margin-left: auto;
}

.user-item__badge {
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.92);
}

.user-item__group {
  font-size: 0.72rem;
  color: #cbd5f5;
}

.user-item__right {
  /* antes tenía margin-left:auto, ahora ya no */
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.user-item__status {
  font-size: 0.72rem;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  border: 1px solid transparent;
}

.user-item__status.is-active {
  border-color: rgba(34, 197, 94, 0.9);
  color: #bbf7d0;
}

.user-item__status.is-inactive {
  border-color: rgba(248, 113, 113, 0.9);
  color: #fecaca;
}

.user-item__edit-btn {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  background: rgba(15, 23, 42, 0.96);
  color: #e5e7eb;
  width: 30px;
  height: 30px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition:
    background 0.16s ease,
    transform 0.1s ease,
    box-shadow 0.16s ease;
}

.user-item__edit-btn:hover {
  background: rgba(15, 23, 42, 1);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.95);
}

.user-item__edit-btn:active {
  transform: translateY(0);
  box-shadow: 0 5px 16px rgba(15, 23, 42, 1);
}

/* 📱 Ajustes para móvil */
@media (max-width: 640px) {
  /* Ocultar por completo el bloque del rol/grupo en móvil
     para que el avatar+texto aprovechen todo el ancho */
  .user-item__middle {
    display: none;
  }

  .user-item__left {
    flex: 1;
  }

  /* Hacemos fade del texto hacia la derecha para que no empuje
     al estado ni al lápiz */
  .user-item__info {
    position: relative;
    overflow: hidden;
    padding-right: 14px; /* pequeño espacio para el fade */
  }

  .user-item__info::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 32px;
    height: 100%;
    pointer-events: none;
    /* mismo tono que el fondo del item, degradando a transparente */
    background: linear-gradient(
      to left,
      rgba(15, 23, 42, 0.86),
      rgba(15, 23, 42, 0)
    );
  }

  .user-item__name,
  .user-item__email {
    white-space: nowrap;
  }
}

</style>
